import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar } from './ui/calendar';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar as CalendarIcon, Clock, MapPin, CheckCircle } from 'lucide-react';
import { timeSlots, serviceAreas } from '../utils/mockData';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reservation = () => {
  const { t } = useLanguage();
  
  // Services for reservation dropdown - will be translated
  const getReservationServices = () => [
    { id: 1, name: t('reservationKitBeginner'), price: 249.99 },
    { id: 2, name: t('reservationKitComfort'), price: 399.99 },
    { id: 3, name: t('reservationKitPremium'), price: 599.99 },
    { id: 4, name: t('reservationCustomKit'), price: 0.00 },
    { id: 5, name: t('reservationInstallOnly'), price: 99.99 },
    { id: 6, name: t('reservationPremiumInstallClient'), price: 199.99 },
    { id: 7, name: t('reservationRemoteConfig'), price: 49.99 }
  ];
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedService, setSelectedService] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedService || !selectedTime || !selectedArea || !name || !email || !phone || !address) {
      toast.error(t('fillAllFields'));
      return;
    }

    try {
      // Format date to YYYY-MM-DD
      const formattedDate = selectedDate.toISOString().split('T')[0];
      
      // Create reservation via API
      const reservationData = {
        date: formattedDate,
        service_id: parseInt(selectedService),
        time: selectedTime,
        area: selectedArea,
        name,
        email,
        phone,
        address
      };

      const response = await axios.post(`${API}/reservations`, reservationData);
      
      const reservation = response.data;
      
      toast.success(
        `${t('reservationConfirmed')} ${reservation.confirmation_number}`,
        { duration: 5000 }
      );
      
      // Show additional info
      setTimeout(() => {
        toast.info(t('confirmationEmailSent'), {
          duration: 5000
        });
      }, 1000);

      // Reset form
      setSelectedDate(null);
      setSelectedService('');
      setSelectedTime('');
      setSelectedArea('');
      setName('');
      setEmail('');
      setPhone('');
      setAddress('');
      
    } catch (error) {
      console.error('Reservation error:', error);
      toast.error(t('reservationError'));
    }
  };

  const RESERVATION_SERVICES = getReservationServices();
  const selectedServiceDetails = RESERVATION_SERVICES.find(s => s.id === parseInt(selectedService));

  return (
    <section id="reservation" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-blue-100 text-blue-700 mb-4">{t('reservation')}</Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('bookYourInstallation')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('chooseDateTime')}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Side - Calendar & Time Selection */}
            <Card className="shadow-xl border-2">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900 flex items-center">
                  <CalendarIcon className="w-6 h-6 mr-2 text-blue-600" />
                  {t('selectDateTime')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Calendar */}
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today || date.getDay() === 0;
                    }}
                    className="rounded-md border"
                  />
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div className="space-y-3">
                    <Label className="text-lg font-semibold flex items-center text-gray-900">
                      <Clock className="w-5 h-5 mr-2 text-blue-600" />
                      {t('appointmentTime')}
                    </Label>
                    <div className="grid grid-cols-4 gap-2">
                      {timeSlots.map((time) => (
                        <Button
                          key={time}
                          type="button"
                          variant={selectedTime === time ? 'default' : 'outline'}
                          className={`${
                            selectedTime === time
                              ? 'bg-blue-600 hover:bg-blue-700 text-white'
                              : 'border-blue-600 text-blue-600 hover:bg-blue-50'
                          }`}
                          onClick={() => setSelectedTime(time)}
                        >
                          {time}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right Side - Service & Contact Info */}
            <Card className="shadow-xl border-2">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">{t('reservationInfo')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Service Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="service" className="text-gray-900 font-semibold">
                      {t('desiredService')} *
                    </Label>
                    <Select value={selectedService} onValueChange={setSelectedService}>
                      <SelectTrigger id="service" className="w-full">
                        <SelectValue placeholder={t('chooseService')} />
                      </SelectTrigger>
                      <SelectContent>
                        {RESERVATION_SERVICES.map((service) => (
                          <SelectItem key={service.id} value={service.id.toString()}>
                            {service.name} - ${service.price === 0 ? t('free') : service.price}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Service Details */}
                  {selectedServiceDetails && (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 space-y-2">
                      <p className="font-semibold text-gray-900">{selectedServiceDetails.name}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-blue-600">
                          {selectedServiceDetails.price === 0 ? t('free') : `$${selectedServiceDetails.price}`}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Area Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="area" className="text-gray-900 font-semibold flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-blue-600" />
                      {t('serviceArea')} *
                    </Label>
                    <Select value={selectedArea} onValueChange={setSelectedArea}>
                      <SelectTrigger id="area" className="w-full">
                        <SelectValue placeholder={t('selectCity')} />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceAreas.map((area) => (
                          <SelectItem key={area} value={area}>
                            {area}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4 pt-4 border-t border-gray-200">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-900 font-semibold">{t("fullName")} *</Label>
                      <input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors"
                        placeholder={t("fullNamePlaceholder")}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-900 font-semibold">Email *</Label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors"
                        placeholder={t("emailPlaceholder")}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-900 font-semibold">{t("phone")} *</Label>
                      <input
                        type="tel"
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors"
                        placeholder={t("phonePlaceholder")}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address" className="text-gray-900 font-semibold">{t("address")} *</Label>
                      <input
                        type="text"
                        id="address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors"
                        placeholder={t("addressPlaceholder")}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    {t("confirmReservation")}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    * Tous les champs sont obligatoires. Vous recevrez une confirmation par email.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reservation;
